/**
 * Where the full-page copilot lives: `/app/{appId}/console` builds that application,
 * `/console` works for the account (find, create and open applications).
 */
import { matchPath } from 'react-router-dom';
import { scopeForPath } from './route-action';

export const ACCOUNT_COPILOT_PATH = '/console';

export function copilotPathFor(appId) {
    return appId ? `/app/${encodeURIComponent(appId)}/console` : ACCOUNT_COPILOT_PATH;
}

export function isCopilotPath(pathname) {
    return Boolean(matchPath({ path: ACCOUNT_COPILOT_PATH }, pathname || '')
        || matchPath({ path: '/app/:appId/console' }, pathname || ''));
}

/**
 * A ROUTE action seen from the copilot page. The page is the copilot, so it only moves
 * when the action leaves the current scope (e.g. into an application the copilot just
 * created): then it opens that scope's copilot page. A route inside the same scope stays
 * a link in the message, and the conversation is not interrupted.
 *
 * @returns {{ path: string, scope: string } | null} where to go, or null to stay
 */
export function copilotRouteFor(actionPath, currentScope, accountId) {
    const target = scopeForPath(actionPath, accountId);
    if (!target || target === currentScope) return null;
    const appMatch = matchPath({ path: '/app/:appId/*' }, actionPath) || matchPath({ path: '/app/:appId' }, actionPath);
    return { path: copilotPathFor(appMatch?.params?.appId || null), scope: target };
}

/** Resource kinds from `resource.changed` events -> the right panel section they belong to. */
export const CHANGE_SECTION = {
    tool: 'tools',
    codehook: 'tools',
    skill: 'skills',
    mcp_server: 'mcpServers',
    playbook: 'playbooks',
    workflow: 'workflows',
    knowledge: 'knowledge',
    instructions: 'instructions',
    settings: 'instructions',
    application: 'applications',
};

/**
 * Everything the copilot changed in this conversation, newest last, de-duplicated by
 * kind + name: `{ kind, action, name, section }`.
 */
export function changesOf(messages) {
    const seen = new Map();
    (Array.isArray(messages) ? messages : []).forEach(message => {
        (Array.isArray(message?.changes) ? message.changes : []).forEach(change => {
            if (!change || typeof change !== 'object') return;
            const kind = String(change.kind || '');
            const name = change.name == null ? '' : String(change.name);
            const key = `${kind}:${name.toLowerCase()}`;
            seen.delete(key);
            seen.set(key, { kind, action: String(change.action || ''), name, section: CHANGE_SECTION[kind] || null });
        });
    });
    return [...seen.values()];
}

/** Left-rail nav hrefs to blink for a `resource.changed` event. */
const NAV_HREF_FOR_KIND = {
    playbook: '/playbooks',
    workflow: '/journey',
    knowledge: '/contents',
    tool: '/agent',
    codehook: '/agent',
    skill: '/agent',
    mcp_server: '/agent',
    instructions: '/agent',
    settings: '/agent',
};

export function navFlashKeys(detail) {
    const href = NAV_HREF_FOR_KIND[detail?.kind];
    return href ? [href] : [];
}

/** Resource-panel keys to blink: the section, and `section:name` for the changed item. */
export function panelFlashKeys(detail) {
    const section = CHANGE_SECTION[detail?.kind];
    if (!section) return [];
    const name = detail?.name == null ? '' : String(detail.name).toLowerCase();
    return name ? [section, `${section}:${name}`] : [section];
}
